import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Loader from "../../../core/common/loader";
import api from "../../../services/api";
import CommonSelect from "../../../core/common/commonSelect";
import HeadCountReport from "./HeadCountReport";
import AttritionReport from "./AttritionReport";
import { all_routes } from "../../router/all_routes";
import { company_short_name } from "../../../environment";
import { truncateCompanyText } from "../../../helper/company_helper";

type ReportType = "headcount" | "attrition";
type CompanyType = {
  company_id: string;
  name: string;
  company_type: string;
};

const AnalyticsDashboard: React.FC = () => {
  const signature = localStorage.getItem("signature");
  const user = useSelector((state: any) => state?.user?.user?.userDetails);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportType>("headcount");
  const [selectedReportOption, setSelectedReportOption] = useState<any>({
    value: "headcount",
    label: "Head Count Report"
  });
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [companyOptions, setCompanyOptions] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyType | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        await updateUI();
      } catch (err: any) {
        console.error("Error fetching dashboard data:", err);
      }
    };

    fetchDashboardData();
  }, []);

  // Report type options
  const reportTypeOptions = useMemo(() => [
    { value: "headcount", label: "Head Count Report" },
    { value: "attrition", label: "Attrition Report" }
  ], []);

  // Fetch all initial data
  const updateUI = async () => {
    setIsLoading(true);
    setIsLoadingCompanies(true);

    try {
      const [getAllCompanyListResponse] = await Promise.all([
        api.post("userService/getAllCompanyList", { signature }),
      ]);

      const companiesData = getAllCompanyListResponse.data.data.data || [];
      setCompanyOptions(companiesData);

      if (companiesData.length > 0) {
        const firstCompany = companiesData[0];
        setSelectedCompany({
          company_id: firstCompany.id,
          name: firstCompany.name,
          company_type: firstCompany.type
        });
      }

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
      setIsLoadingCompanies(false);
    }
  };

  useEffect(() => {
    if (selectedCompany) {
      fetchEmployeeStatistics();
    }
  }, [selectedCompany]);

  const fetchEmployeeStatistics = async () => {
    if (!selectedCompany?.company_id) return;

    setIsLoading(true);
    try {
      const response = await api.post("userService/getEmployeeStatisticsAndDetails", {
        signature,
        companyId: selectedCompany.company_id,
        companyType: selectedCompany.company_type
      });

      if (response.data && response.data.data.data) {
        setEmployeeData(response.data.data.data);
      }
    } catch (error) {
      console.error("Error fetching employee statistics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle company selection
  const handleCompanySelection = async (selectedOption: any) => {
    if (selectedOption?.value) {
      const company = companyOptions.find(opt => opt.id === selectedOption.value);
      if (company) {
        setSelectedCompany({
          company_id: company.id,
          name: company.name,
          company_type: company.type
        });
      }
    }
  };

  const handleReportTypeSelection = (selectedOption: any) => {
    setSelectedReport(selectedOption.value);
    setSelectedReportOption(selectedOption);
  };

  useEffect(() => {
    if (!isLoadingCompanies) {
      setSelectedReportOption({ value: "headcount", label: "Head Count Report" });
      setSelectedReport("headcount");
    }
  }, [isLoadingCompanies]);

  const companyChooseFilter = companyOptions.map(company => ({
    value: company.id,
    label: `${truncateCompanyText(company.name, 20)} - ${company.type}`,
    companyName: company.name,
    companyType: company.type
  }));

  // Get selected company option
  const selectedCompanyOption = useMemo(() => {
    if (!selectedCompany) return undefined;
    return {
      value: `${selectedCompany.company_id} - ${selectedCompany.company_type}`,
      label: `${truncateCompanyText(selectedCompany.name, 20)} - ${selectedCompany.company_type}`,
    };
  }, [selectedCompany]);

  if (isLoading) return <Loader />;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header Section */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Analytics Dashboard</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.analyticsDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Analytics Dashboard</li>
                <li className="breadcrumb-item active">
                  {selectedReport === "headcount" ? "Head Count Report" :
                    selectedReport === "attrition" ? "Head Count Report" : "Attrition Report"}
                </li>
              </ol>
            </nav>
          </div>

          {/* Controls */}
          <div className="d-flex align-items-center gap-3">
            <div className="me-3" style={{ minWidth: '200px' }}>

              <CommonSelect
                className="select"
                options={reportTypeOptions}
                defaultValue={selectedReportOption}
                onChange={handleReportTypeSelection}
                isDisabled={isLoadingCompanies}
              />
            </div>

            <div className="me-3" style={{ minWidth: '250px' }}>
              <CommonSelect
                className="select"
                options={companyChooseFilter}
                defaultValue={selectedCompanyOption}
                onChange={handleCompanySelection}
                isDisabled={isLoadingCompanies}
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={fetchEmployeeStatistics}
              disabled={!selectedCompany || isLoading}
            >
              <i className="ti ti-refresh me-2"></i>
              Refresh
            </button>
          </div>
        </div>

        {selectedCompany ? (
          <>
            {selectedReport === "headcount" && (
              <HeadCountReport
                employeeData={employeeData}
                selectedCompany={selectedCompany}
                isLoading={isLoading}
                onRefresh={fetchEmployeeStatistics}
              />
            )}


            {selectedReport === "attrition" && (
              <AttritionReport
                employeeData={employeeData}
                selectedCompany={selectedCompany}
                isLoading={isLoading}
              />
            )}
          </>
        ) : (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-building-community display-4 text-muted mb-3" />
              <h5 className="mb-2">No Company Selected</h5>
              <p className="text-muted">Please select a company to view analytics</p>
              <button
                className="btn btn-primary mt-3"
                onClick={updateUI}
                disabled={isLoadingCompanies}
              >
                {isLoadingCompanies ? 'Loading Companies...' : 'Load Companies'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2025 © {company_short_name}</p>
        <p>
          Designed & Developed By{" "}
          <Link to="#" className="text-primary">
            {company_short_name}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;